/**
 * Подключаем изоморфный полифил для `requestAnimationFrame` который необходим
 * для правильной работы `react@16` и не входит в `core-js`.
 */
import 'raf/polyfill';

import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { global } from 'styled-jsx/css';

import { Helmet } from 'core/libs/helmet';
import { withRouter } from 'core/libs/router';

import coreApisFactory, { knownApiContextTypes } from 'core/api';

import EmptyWrapper from 'core/components/EmptyWrapper';
import PremiumTopicHeader from 'core/components/PremiumTopicHeader';
import GlobalTopicProvider from 'core/components/GlobalTopicProvider';
import Counters from 'core/components/Counters';
import AmpCounters from 'core/components/Counters/Amp';
import PuidsProvider from 'core/components/Ad/puids/PuidsProvider';
import UserProvider from 'core/components/User/UserProvider';
import UnityIdProvider from 'core/components/UnityId/UnityIdProvider';
import PushWoosh from 'core/components/PushWoosh';
import RamblerPush from 'core/components/RamblerPush';
import LastTopicsSaver from 'core/components/OfflinePage/LastTopicsSaver';
import { ScrollToSiteProvider } from 'core/components/ScrollToSite';

import withRequestHeaders from 'core/hocs/withRequestHeaders';

import ScrollToTop from './ScrollToTop';
import CommonSiteWrapper from './SiteWrapper';
import RequestIdProvider from './requestId/RequestIdProvider';

import themesFactory from 'core/themes';

import counterFactory from 'core/utils/counter';
import valueEmitterFactory from 'core/utils/value-emitter';
import { resolve as resolveEnv } from 'core/utils/env';

import adsPropTypes, { puidsPropTypes } from 'core/utils/prop-types/ads';
import counterPropTypes from 'core/utils/prop-types/counter';
import valueEmitterPropTypes from 'core/utils/prop-types/value-emitter';
import requestHeadersPropTypes from 'core/utils/prop-types/request-headers';
import pushwooshPropTypes from 'core/utils/prop-types/pushwoosh';
import ramblerPushPropTypes from 'core/utils/prop-types/rambler-push';
import apisConfigPropTypes from 'core/utils/prop-types/api';
import breakpointPropTypes from 'core/utils/prop-types/breakpoint';

import { DESKTOP, MOBILE } from 'core/components/breakpoint/values';

import GlobalStyles from 'core/components/GlobalStyles';

function toDashCase(camelCaseStr) {
  return camelCaseStr.replace(/([A-Z])/g, match => {
    return `-${match[0].toLowerCase()}`;
  });
}

function getThemes(themes, isMobile) {
  return Object.keys(themes)
    .reduce((group, currentTheme) => {
      const breakpointTheme = isMobile ? 'mobile' : 'base';
      group[currentTheme] = themes[currentTheme][breakpointTheme];
      return group;
    }, {});
}

/**
 * Преобразование дескриптора кастомных шрифтов из темы в css стили
 * для `@font-face`.
 *
 * Поддерживается преобразование всех свойств описанных в спецификации
 * https://goo.gl/vKeS79 в общем виде.
 */
function prepareFontFaceRules(fontFace) {
  /*
  * Выполнении рекомендации Google Lighthouse "Ensure text remains visible during webfont load"
  * Пока шрифты загружаются тексты демонстрируются с дефолтным шрифтом
  * Как только шрифт загрузится, то он заменяет собой дефолтный
  */
  const fontFaceWithSwap = {
    ...fontFace,
    fontDisplay: 'swap',
  };
  return Object
    .keys(fontFaceWithSwap)
    .map(name => `${toDashCase(name)}:${fontFaceWithSwap[name]}`)
    .join(';');
}

/**
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 */

class App extends Component {
  static propTypes = {
    siteName: PropTypes.string,
    /**
     * Конфигурация базовых путей api, которые должны быть
     * доступны приложению.
     */
    coreApisConfig: apisConfigPropTypes,

    /**
     * Мета теги сайта. Вставляются в react-helmet
     */
    defaultSiteMeta: PropTypes.object,

    children: PropTypes.node,

    /**
     * Хост по которому сайт будет доступен.
     * Необходим для резолвинга урлов ссылок и не только.
     */
    host: PropTypes.string.isRequired,

    /**
     * Название организации и лого (сайта)
     * Необходим для валидации Schema.Org
     */
    schemaOrg: PropTypes.shape({
      organizationName: PropTypes.string.isRequired,
      alternateName: PropTypes.string,
      organizationLogoUrl: PropTypes.string.isRequired,
      organizationLogoWidth: PropTypes.number.isRequired,
      organizationLogoHeight: PropTypes.number.isRequired,
    }),

    /**
     * Язык приложения.
     * По умолчанию `ru`.
     */
    lang: PropTypes.string,

    /**
     * Имя активной темы сайта.
     * Имя — свойство объекта `themes`.
     */
    theme: PropTypes.string.isRequired,

    /**
     * Делаем простую валидацию на наличие тем.
     * Сами атомы будут валидироваться в компонентах которые их используют.
     */
    themes: PropTypes.object.isRequired,

    /**
     * URL до инстанса thumbor.
     * Он необходим для оптимизации и постобработки изображений.
     */
    thumborHost: PropTypes.string,

    /**
     * Параметры видео-хостеров
     */
    videoHosting: PropTypes.objectOf(PropTypes.object),

    /**
     * URL до webdav с изображениями.
     */
    webdavHost: PropTypes.string,

    /**
     * URL для отправки ошибок.
     */
    erratumUrl: PropTypes.string,

    /**
     * Параметр для разделения внешних и внутренних проектов.
     */
    isUnityProject: PropTypes.bool,

    /**
     * код для pushWoosh
     */
    pushWoosh: pushwooshPropTypes,

    /**
     * коды для rambler push
     */
    ramblerPush: ramblerPushPropTypes,

    /**
     * Конфигурация рекламных блоков.
     *
     * Подробности смотри в `utils/prop-types/ads.js`.
     */
    ads: adsPropTypes(),

    /**
     * `ramblerCommentsAppId` нужно запрашивать для каждого проекта
     * у коллег из Рамблер.Комментарии.
     */
    ramblerCommentsAppId: PropTypes.number,

    /**
     * Ссылки, которые не будут обрабатываться CSR, и будут ранжироваться поисковыми системами.
     */
    specialLinks: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
      ]),
    ),

    /**
     * Внешние ссылки, которые всегда будут ранжироваться поисковыми системами.
     */
    followExternalLinks: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
      ]),
    ),

    features: PropTypes.shape({
      /**
       * Флаг отвечающий за поддержку ретина устройств
       */
      enableRetina: PropTypes.bool,
      /**
       * Параметр определяет нужно ли использовать blur в thumbor.
       */
      disableBlurPreview: PropTypes.bool,
      isNewVersionPremiumTopic: PropTypes.bool,
      /**
       * Флаг для отключения AMP версии на сайте.
       * Например, убирает канонические ссылки для `link[rel='amphtml']`
       */
      disableAmp: PropTypes.bool,
      /**
       * Флаг для включения/выключения управления партнерками через Fenwick
       */
      enableFenwick: PropTypes.bool,

      /**
       * Разрешить небезопасные ссылки.
       * Правило для target='_blank':
       * `rel='noopener'` защищает от атак (договорились выставлять всегда)
       * `rel='noreferrer'` скрывает источник трафика
       *
       * По умолчанию не разрешаем, следуя рекомендованным правилам безопасности
       * https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
       */
      disableNoreferrerLink: PropTypes.bool,

      /**
       * Включает авторизацию unityId (sberID).
       * Выключает авторизацию по ramblerID
       */
      enableUnityId: PropTypes.bool,
    }),

    amp: PropTypes.bool,

    /**
     * ID счётчиков, которые будут добавлены на страницы сайта.
     */
    counterIds: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array,
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
    ])),

    /**
     * Конфигурация AMP страниц.
     */
    ampConfig: PropTypes.shape({
      /**
       * Дата в формате ISO 8601. Если топик опубликован до этой даты,
       * то для него не будет проставлена ссылка amphtml.
       * При этом амп страница будет доступна по прямой ссылке - /amp/link.htm
       */
      startAmpDate: PropTypes.string,
    }),

    /**
     * Путь позволяющий разделить сервис и журнал
     * и сформировать корректные urls для рубрик и тегов
     */
    mediaUrlPrefix: PropTypes.string,

    spammer: PropTypes.shape({
      /**
       * Ключ проекта &CRM.
       * Подробности: https://confluence.rambler-co.ru/pages/viewpage.action?pageId=35684697
       */
      ramblerCrmProjectKey: PropTypes.string,
      termsUrl: PropTypes.string,
      digestKey: PropTypes.string,
    }),

    ramblerId: PropTypes.shape({
      rname: PropTypes.string,
    }),

    /**
     * Можно определить пуиды для всей страницы чтобы они не запрашивались
     * из апишки
     */
    puids: puidsPropTypes,

    location: PropTypes.shape({
      pathname: PropTypes.string,
      hash: PropTypes.string,
      key: PropTypes.string,
      search: PropTypes.string,
      state: PropTypes.object,
    }),
    history: PropTypes.object,
    themesController: PropTypes.shape({
      fork: PropTypes.func,
      get: PropTypes.func,
      has: PropTypes.func,
      list: PropTypes.func,
      subscribe: PropTypes.func,
      use: PropTypes.func,
    }),
    helmetOnChangeEmitter: valueEmitterPropTypes,
    counterIssuer: counterPropTypes,
    socials: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        link: PropTypes.string,
      })
    ),
    scrollToSite: PropTypes.object,
    splitTest: PropTypes.shape({
      name: PropTypes.string,
      miscTypes: PropTypes.shape({
        1: PropTypes.number,
        2: PropTypes.number,
      }),
    }),
    requestHeaders: requestHeadersPropTypes,

    /**
     * Коллбек для вызова в соответствующем методе App.
     * В качестве аргумента ему передаётся легаси-контекст, формируемый в App
     */
    getChildContext: PropTypes.func,
  };

  static contextTypes = {
    initialBreakpoint: breakpointPropTypes(),
  };

  static childContextTypes = {
    siteName: PropTypes.string,
    ads: adsPropTypes(),
    lang: PropTypes.string,
    host: PropTypes.string,
    themes: PropTypes.object,
    pushWoosh: pushwooshPropTypes,
    ramblerPush: ramblerPushPropTypes,
    webdavHost: PropTypes.string,
    thumborHost: PropTypes.string,
    specialLinks: PropTypes.array,
    followExternalLinks: PropTypes.array,
    counterIssuer: counterPropTypes,
    defaultSiteMeta: PropTypes.object,
    ramblerCommentsAppId: PropTypes.number,
    helmetOnChangeEmitter: valueEmitterPropTypes,
    videoHosting: PropTypes.objectOf(PropTypes.object),
    features: PropTypes.object,
    amp: PropTypes.bool,
    ampConfig: PropTypes.object,
    schemaOrg: PropTypes.object,
    counterIds: PropTypes.object,
    chameleon: PropTypes.object,
    mediaUrlPrefix: PropTypes.string,
    spammer: PropTypes.object,
    ramblerId: PropTypes.object,
    socials: PropTypes.array,
    erratumUrl: PropTypes.string,
    requestHeaders: requestHeadersPropTypes,
    isUnityProject: PropTypes.bool,
    scrollToSite: PropTypes.object,
    splitTest: PropTypes.object,
    initialBreakpoint: breakpointPropTypes(),
    ...knownApiContextTypes,
  };

  constructor(props, context) {
    super(props, context);
    const { initialBreakpoint: breakpoint } = this.context;
    const { themes, theme } = props;

    const isMobile = breakpoint === MOBILE;
    this.finallyThemes = getThemes(themes, isMobile);

    this.state = {
      counterIssuer: counterFactory(),
      themesController: themesFactory(this.finallyThemes, theme),
      helmetOnChangeEmitter: valueEmitterFactory(),
    };
  }

  getChildContext() {
    const {
      siteName,
      amp,
      ads,
      host,
      lang,
      features,
      pushWoosh,
      ramblerPush,
      isUnityProject,
      webdavHost,
      thumborHost,
      videoHosting,
      defaultSiteMeta,
      ramblerCommentsAppId,
      specialLinks,
      followExternalLinks,
      schemaOrg,
      ampConfig,
      counterIds,
      mediaUrlPrefix,
      spammer,
      ramblerId,
      socials,
      erratumUrl,
      coreApisConfig,
      scrollToSite,
      splitTest,
      requestHeaders,
      getChildContext,
    } = this.props;

    const { initialBreakpoint } = this.context;

    const {
      counterIssuer,
      themesController,
      helmetOnChangeEmitter,
    } = this.state;

    const context = {
      siteName,
      amp,
      ads,
      host,
      lang: lang || 'ru',
      features: { ...features },
      pushWoosh,
      ramblerPush,
      isUnityProject,
      webdavHost,
      thumborHost,
      videoHosting,
      defaultSiteMeta: { ...defaultSiteMeta },
      ramblerCommentsAppId,
      specialLinks,
      followExternalLinks,
      themes: themesController,
      counterIssuer,
      helmetOnChangeEmitter,
      schemaOrg,
      ampConfig: { ...ampConfig },
      counterIds,
      mediaUrlPrefix,
      spammer: { ...spammer },
      ramblerId: { ...ramblerId },
      socials,
      erratumUrl,
      scrollToSite,
      splitTest,
      requestHeaders,
      initialBreakpoint,
      ...coreApisFactory({ coreApisConfig, requestHeaders }),
    };

    if (typeof getChildContext === 'function') getChildContext(context);

    return context;
  }

  /**
   * Перерисовываем вью только если изменились пропсы,
   * которые учавствуют в отрисовке.
   */
  shouldComponentUpdate(nextProps) {
    const { lang, themes, children, schemaOrg } = this.props;
    const { lang: langNext, themes: themesNext, children: childrenNext, schemaOrg: schemaOrgNext } = nextProps;

    return lang !== langNext || themes !== themesNext || children !== childrenNext || schemaOrg !== schemaOrgNext;
  }

  render() {
    const {
      lang = 'ru',
      children,
      defaultSiteMeta = {},
      amp,
      counterIds,
      location,
      history,
      ads,
      host,
      puids,
      ramblerId,
      pushWoosh,
      ramblerPush,
      isUnityProject,
      schemaOrg: {
        organizationName,
        alternateName,
      },
      features: {
        isNewVersionPremiumTopic,
        enableUnityId,
      },
    } = this.props;

    const { themesController, helmetOnChangeEmitter } = this.state;

    const { initialBreakpoint: breakpoint } = this.context;

    const isDesktop = breakpoint === DESKTOP;

    const { theme } = themesController.get();

    const fontFaces = Object
      .keys(this.finallyThemes)
      .map(name => this.finallyThemes[name].fonts.faces)
      .filter(Boolean)
      .reduce((prev, next) => prev.concat(next), [])
      .map(fontFace => `@font-face { ${prepareFontFaceRules(fontFace)} }`)
      .join('');

    const SiteWrapper = amp ? EmptyWrapper : CommonSiteWrapper;
    const letterSpacing = theme.texts.body.letterSpacing;

    const globalStyles = global`${fontFaces}
      body
        background-color ${theme.colors.layout}
        font ${theme.texts.body.font}
        color ${theme.texts.body.color}
        letter-spacing ${letterSpacing || 'initial'}
    `;

    const CountersComponent = amp ? AmpCounters : Counters;

    const viewport = isDesktop
      ? `width=${parseInt(theme.layout.viewportWidth || theme.layout.maxWidth, 10)}`
      : 'width=device-width, minimum-scale=1, initial-scale=1';

    const helmetParams = {
      ...defaultSiteMeta,
      meta: [
        { 'http-equiv': 'x-ua-compatible', content: 'ie=edge' },
        { name: 'viewport', content: viewport },
        { property: 'og:image', content: `https://${host}/icons/og-image.png` },
        { name: 'theme-color', content: theme.colors.theme },
        { name: 'msapplication-TileColor', content: theme.colors.theme },
        { name: 'referrer', content: 'no-referrer-when-downgrade' },
        ...(defaultSiteMeta.meta || []),
      ],
      link: [
        { rel: 'icon', type: 'image/svg', sizes: '48x48', href: '/favicon.svg' },
        { rel: 'mask-icon', href: '/icons/safari-pinned-tab.svg', color: theme.colors.theme },
        { rel: 'apple-touch-icon', href: '/icons/apple-touch-icon.png' },
        { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
        { rel: 'manifest', href: resolveEnv({
          /**
           * Добавляем хеш от содержимого манифеста, для сброски кеша на клиенте.
           */
          '*': '/manifest.json?v=' + process.env.MANIFEST_HASH,

          /**
           * Чтоб была возможность нормально тестировать пуши на стейджах
           * заводим для них отдельный `manifest-stage.json` со спец
           * параметром `gcm_sender_id` для тестирования.
           */
          'staging': '/manifest-stage.json?v=' + process.env.MANIFEST_HASH,
        }) },
        ...(defaultSiteMeta.link || []),
      ],
    };

    const jsonLd = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'url': `https://${host}/`,
        'logo': `https://${host}/icons/apple-touch-icon.png`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': organizationName,
        'url': `https://${host}/`,
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `https://${host}/search?query={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
        ...(alternateName && { 'alternateName': alternateName }),
      },
    ];

    const UnityIdWrapper = !enableUnityId ? EmptyWrapper : UnityIdProvider;
    const UserWrappper = !ramblerId ? EmptyWrapper : UserProvider;
    const PuidsWrapper = !ads ? EmptyWrapper : PuidsProvider;
    const isMain = location.pathname === '/';
    const TopicProvider = isNewVersionPremiumTopic ? GlobalTopicProvider : EmptyWrapper;

    return (
      <Fragment>
        <style jsx global>{globalStyles}</style>
        <Helmet {...helmetParams}>
          <html lang={lang} />
          <body className={breakpoint} />
          {isMain && isUnityProject &&
            jsonLd.map((json, i) =>
              <script type='application/ld+json' key={i}>{JSON.stringify(json)}</script>
            )
          }
        </Helmet>
        <UserWrappper>
          <UnityIdWrapper>
            <PuidsWrapper
              {...puids && { puids }} // передаем пуиды, только если они есть, чтобы не переопределить puids из кеша
              link={location.pathname}
            >
              <RequestIdProvider location={location}>
                <TopicProvider location={location} history={history}>
                  {isNewVersionPremiumTopic && (
                    <PremiumTopicHeader />
                  )}
                  <SiteWrapper
                    theme={theme}
                    isDesktop={isDesktop}
                  >
                    <ScrollToSiteProvider>
                      {children}
                    </ScrollToSiteProvider>
                  </SiteWrapper>
                </TopicProvider>
              </RequestIdProvider>
            </PuidsWrapper>
          </UnityIdWrapper>
          <CountersComponent counterIds={counterIds} />
        </UserWrappper>
        {
          /**
           * Так как у `react-helmet` есть проблема с переопределением пропса
           * `onChangeClientState` мы обходим ее с помощью вспомогательного
           * компонента `components/App/HelmetStateChange` который
           * реализовывает очередь подписчиков на это событие.
           *
           * Важно чтоб этот инстанс `Helmet` был последним элементом.
           *
           * Так же важно следить во время code review чтоб никто не пытался
           * использовать `onChangeClientState` в других местах, так как
           * он работать не будет.
           *
           * TODO: Переделать это ad-hoc решение на нормальное после
           * исправления https://github.com/nfl/react-helmet/issues/328
           */
        }
        <Helmet onChangeClientState={helmetOnChangeEmitter.set} />
        <ScrollToTop />
        {pushWoosh && <PushWoosh />}
        {ramblerPush && <RamblerPush />}
        <LastTopicsSaver />
        <GlobalStyles theme={theme} />
      </Fragment>
    );
  }
}

// Добавляем отслеживание роутинга. Это нужно для генерации рантайма.
export default withRequestHeaders(withRouter(App));
